import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addToBag,
  bagCount,
  bagSubtotal,
  changeQty as changeQtyPure,
  clampQty,
  removeItem,
  type AddToBagInput,
  type BagItem,
} from '../lib/bag';

// Estado da sacola encapsulado. A lógica de fusão/clamp/derivados vive em lib/bag
// (pura, testável); aqui só ligamos ao React.
export interface UseBag {
  bag: BagItem[];
  count: number;
  subtotal: number;
  add: (input: AddToBagInput) => void;
  changeQty: (key: string, delta: number) => void;
  remove: (key: string) => void;
  clear: () => void;
}

// QA L5 #9 — PERSISTÊNCIA da sacola. Antes, um reload/pull-to-refresh (comum no celular) zerava o
// carrinho: o cliente perdia tudo que montou. Agora a sacola sobrevive ao recarregamento.
//  • mesa → sessionStorage: escopo de UMA sessão de mesa; some ao fechar a aba (não vaza p/ o próximo cliente).
//  • delivery → localStorage com TTL: sobrevive a fechar a aba (voltar mais tarde), mas expira p/ não
//    ressuscitar um carrinho obsoleto (preços/itens podem ter mudado). O backend é a autoridade de preço
//    na finalização; o TTL só evita exibir um carrinho velho.
export interface BagPersist {
  /** 'session' = sessionStorage (mesa) · 'local' = localStorage (delivery). */
  area: 'session' | 'local';
  /** Chave lógica do carrinho (ex.: `${slug}:delivery`). Namespaceada internamente. */
  key: string;
  /** Descarta o carrinho persistido se mais velho que isto (ms). Só faz sentido no 'local'. */
  ttlMs?: number;
}

const NS = 'vitto:bag:';

/** Prefixo de namespace das chaves de persistência da sacola (localStorage/sessionStorage). Exportado
 *  p/ o ErrorBoundary poder limpar TODAS as sacolas persistidas num fallback de erro fatal — sem
 *  precisar saber a chave exata (slug/mesa) da rota em que o erro aconteceu. */
export const BAG_STORAGE_PREFIX = NS;

function storageDe(area: 'session' | 'local'): Storage | null {
  try {
    // Acesso a storage pode LANÇAR (modo restrito/iframe cross-origin/privado): degrada p/ sem persistência.
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Item mínimo válido p/ renderizar: defende contra payload corrompido ou de versão antiga do app.
 *
 * QA CRÍTICO — antes só validava key/qty/unitPrice/produtoId; um item de schema antigo (ou corrompido)
 * SEM `adicionais`/`variacao` no formato certo passava e derrubava a loja inteira em tela branca
 * (`.map` de `undefined` em BagDrawer/order/Checkout, que assumem `adicionais` sempre um array e
 * `variacao` sempre `null` ou `{ id, ... }`). O storage tem TTL de 12h — sobrevive a um deploy que
 * mude o schema — então essa validação é a única linha de defesa antes do ErrorBoundary.
 */
function itemValido(it: unknown): it is BagItem {
  const i = it as BagItem;
  return (
    Boolean(i) &&
    typeof i.key === 'string' &&
    typeof i.qty === 'number' &&
    i.qty > 0 &&
    typeof i.unitPrice === 'number' &&
    Boolean(i.produto) &&
    typeof i.produto.produtoId === 'string' &&
    Array.isArray(i.adicionais) &&
    i.adicionais.every((a) => Boolean(a) && typeof a.id === 'string') &&
    (i.variacao == null || (typeof i.variacao === 'object' && typeof i.variacao.id === 'string'))
  );
}

function ler(persist: BagPersist): BagItem[] {
  const store = storageDe(persist.area);
  if (!store) return [];
  try {
    const bruto = store.getItem(NS + persist.key);
    if (!bruto) return [];
    const dados = JSON.parse(bruto) as { ts?: number; items?: unknown };
    if (!dados || !Array.isArray(dados.items)) return [];
    if (
      persist.ttlMs != null &&
      typeof dados.ts === 'number' &&
      Date.now() - dados.ts > persist.ttlMs
    ) {
      store.removeItem(NS + persist.key);
      return [];
    }
    // QA ALTO — itemValido só exige qty>0; sem isto, um item persistido com qty:99999 (ou 2.7, não-inteiro)
    // sobrevivia à validação e fluía intacto até o payload do pedido. Clampa (e arredonda) toda linha
    // reidratada do storage p/ [1, MAX_QTY] — a mesma garantia que addToBag/changeQty já dão a itens
    // adicionados nesta sessão.
    return (dados.items as unknown[]).filter(itemValido).map((i) => ({ ...i, qty: clampQty(i.qty) }));
  } catch {
    return [];
  }
}

function gravar(persist: BagPersist, bag: BagItem[]): void {
  const store = storageDe(persist.area);
  if (!store) return;
  const chave = NS + persist.key;
  try {
    if (bag.length === 0) {
      store.removeItem(chave);
      return;
    }
    store.setItem(chave, JSON.stringify({ ts: Date.now(), items: bag }));
  } catch {
    // Quota cheia / storage indisponível: persistência é best-effort e NUNCA quebra a UI.
  }
}

export function useBag(persist?: BagPersist): UseBag {
  // O descritor pode ser recriado a cada render (objeto literal no chamador); guardamos numa ref
  // p/ o efeito de gravação não depender da identidade dele. slug/mesa são estáveis por montagem.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const [bag, setBag] = useState<BagItem[]>(() => (persist ? ler(persist) : []));

  // Persiste a cada mudança (best-effort). Sem descritor, não toca storage algum.
  useEffect(() => {
    const p = persistRef.current;
    if (p) gravar(p, bag);
  }, [bag]);

  const add = useCallback((input: AddToBagInput) => setBag((b) => addToBag(b, input)), []);
  const changeQty = useCallback(
    (key: string, delta: number) => setBag((b) => changeQtyPure(b, key, delta)),
    []
  );
  const remove = useCallback((key: string) => setBag((b) => removeItem(b, key)), []);
  const clear = useCallback(() => setBag([]), []);

  const count = useMemo(() => bagCount(bag), [bag]);
  const subtotal = useMemo(() => bagSubtotal(bag), [bag]);

  return { bag, count, subtotal, add, changeQty, remove, clear };
}
